'use client'
import { confirmGoogleDrivePickerSelectionAction } from '@/server-actions/knowledge-sources'
import { GOOGLE_DRIVE_FILE_SCOPE, GOOGLE_DRIVE_SUPPORTED_MIME_TYPES } from '@/lib/knowledge-sources/adapters/google-drive-settings'

interface PickerDocument { id: string; name: string }
interface PickerBuilder {
  setDeveloperKey(key: string): PickerBuilder
  setAppId(id: string): PickerBuilder
  setOAuthToken(token: string): PickerBuilder
  setOrigin(origin: string): PickerBuilder
  enableFeature(feature: unknown): PickerBuilder
  addView(view: unknown): PickerBuilder
  setCallback(callback: (data: { action: string; docs?: PickerDocument[] }) => void): PickerBuilder
  build(): { setVisible(visible: boolean): void; dispose(): void }
}
interface GoogleSdk {
  accounts: { oauth2: { initTokenClient(config: {
    client_id: string; scope: string; include_granted_scopes: boolean; hint: string;
    callback(response: { access_token?: string; scope?: string; error?: string }): void;
    error_callback(error: { type: string }): void;
  }): { requestAccessToken(options: { prompt: string }): void } } }
  picker: { PickerBuilder: new () => PickerBuilder; DocsView: new () => {
    setIncludeFolders(value: boolean): unknown; setSelectFolderEnabled(value: boolean): unknown; setMimeTypes(value: string): unknown;
  }; Feature: { MULTISELECT_ENABLED: unknown }; Action: { PICKED: string; CANCEL: string } }
}
const SDK_LOAD_TIMEOUT_MS = 15_000
let sdkPromise: Promise<void> | undefined

export function loadGooglePicker(): Promise<void> {
  sdkPromise ??= Promise.all([
    loadScript('https://accounts.google.com/gsi/client'), loadScript('https://apis.google.com/js/api.js'),
  ]).then(() => new Promise<void>((resolve, reject) => {
    const gapi = (window as unknown as { gapi: { load(name: string, options: { callback(): void; onerror(): void; timeout: number; ontimeout(): void }): void } }).gapi
    gapi.load('picker', { callback: resolve, onerror: () => reject(new Error('Picker could not load')), timeout: SDK_LOAD_TIMEOUT_MS,
      ontimeout: () => reject(new Error('Picker could not load')) })
  })).catch(error => { sdkPromise = undefined; throw error })
  return sdkPromise
}

/** Access tokens stay in this closure, never React Query, storage, URLs, or application logs. */
export function chooseGoogleDriveFiles(config: { connectionId: string; clientId: string; appId: string; developerKey: string; accountLabel: string }): Promise<{ files: PickerDocument[]; selectionId: string | null }> {
  const google = (window as unknown as { google: GoogleSdk }).google
  return new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({ client_id: config.clientId,
      scope: `${GOOGLE_DRIVE_FILE_SCOPE} openid https://www.googleapis.com/auth/userinfo.email`,
      include_granted_scopes: false, hint: config.accountLabel,
      error_callback: () => reject(new Error('Google authorization was cancelled.')),
      callback: response => {
        void (async () => {
          if (!response.access_token || response.error) throw new Error('Google file access was not granted.')
          const scopes = response.scope?.split(/\s+/) ?? []
          const allowed = new Set([GOOGLE_DRIVE_FILE_SCOPE, 'openid', 'email', 'profile', 'https://www.googleapis.com/auth/userinfo.email', 'https://www.googleapis.com/auth/userinfo.profile'])
          if (!scopes.includes(GOOGLE_DRIVE_FILE_SCOPE) || scopes.some(scope => !allowed.has(scope))) throw new Error('Google returned broader permissions. Please connect Google Drive again.')
          const identity = await fetch('https://openidconnect.googleapis.com/v1/userinfo', {
            headers: { Authorization: `Bearer ${response.access_token}` }, cache: 'no-store', signal: AbortSignal.timeout(15_000),
          })
          const account = await identity.json() as { email?: string }
          if (!identity.ok || account.email?.toLowerCase() !== config.accountLabel.toLowerCase()) throw new Error('Choose the same Google account as the connected account.')
          const view = new google.picker.DocsView()
          view.setIncludeFolders(true)
          view.setSelectFolderEnabled(false)
          view.setMimeTypes(GOOGLE_DRIVE_SUPPORTED_MIME_TYPES.join(','))
          const picker = new google.picker.PickerBuilder().setDeveloperKey(config.developerKey).setAppId(config.appId)
            .setOAuthToken(response.access_token).setOrigin(window.location.origin).enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
            .addView(view).setCallback(data => {
              if (data.action === google.picker.Action.PICKED) {
                picker.dispose()
                const files = data.docs ?? []
                if (!files.length) { resolve({ files: [], selectionId: null }); return }
                // Verify in a direct action before returning; the token never enters mutation/query state.
                void confirmGoogleDrivePickerSelectionAction({ connectionId: config.connectionId, pickerToken: response.access_token!, fileIds: files.map(file => file.id) })
                  .then(receipt => { if ('status' in receipt) throw new Error(receipt.errorMessage); resolve({ files, selectionId: receipt.selectionId }) }).catch(reject)
              }
              if (data.action === google.picker.Action.CANCEL) { resolve({ files: [], selectionId: null }); picker.dispose() }
            }).build()
          picker.setVisible(true)
        })().catch(reject)
      },
    })
    client.requestAccessToken({ prompt: '' })
  })
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    const timeout = setTimeout(fail, SDK_LOAD_TIMEOUT_MS)
    script.src = src; script.async = true
    script.onload = () => { clearTimeout(timeout); resolve() }
    script.onerror = fail
    document.head.appendChild(script)

    function fail() {
      clearTimeout(timeout)
      script.onload = null
      script.onerror = null
      script.remove()
      reject(new Error('Google could not load. Check your connection and browser settings.'))
    }
  })
}
